import React from 'react';
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Confirmation card shown inline in the help chat whenever the agent
 * proposes a write action (apply leave, start timer, change task status…).
 *
 * Props:
 *  - action: { id, tool, label, args, fields: [{name, label, display, value}], destructive, warnings }
 *  - status: 'pending' | 'loading' | 'confirmed' | 'cancelled' | 'failed'
 *  - resultMessage: string (shown after confirm/cancel)
 *  - onConfirm(action)
 *  - onCancel(action)
 */
const ActionCard = ({ action, status = 'pending', resultMessage, onConfirm, onCancel }) => {
  if (!action) return null;

  const isDone = status === 'confirmed' || status === 'cancelled' || status === 'failed';
  const isLoading = status === 'loading';

  const headerColour = action.destructive
    ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
    : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700';

  const iconColour = action.destructive
    ? 'text-red-600 dark:text-red-400'
    : 'text-primary-600 dark:text-primary-400';

  return (
    <div
      className={`my-3 rounded-xl border ${headerColour} overflow-hidden`}
      data-testid="action-card"
    >
      <div className="px-4 py-3 flex items-center gap-3">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white dark:bg-secondary-800 shadow-sm ${iconColour}`}>
          {action.destructive ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
            {action.label || 'Confirm action'}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            Review the details and confirm to proceed.
          </p>
        </div>
      </div>

      {action.warnings && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{action.warnings}</span>
        </div>
      )}

      {Array.isArray(action.fields) && action.fields.length > 0 && (
        <div className="px-4 py-3 bg-white dark:bg-secondary-800/40 border-t border-secondary-200 dark:border-secondary-700">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            {action.fields.map((f) => (
              <div key={f.name} className="text-xs">
                <dt className="text-secondary-500 dark:text-secondary-400 font-medium">
                  {f.label || f.name}
                </dt>
                <dd className="text-secondary-900 dark:text-secondary-100 break-words">
                  {f.display ?? String(f.value ?? '')}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="px-4 py-3 border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 flex items-center justify-end gap-2">
        {isDone ? (
          <div className="flex items-center gap-2 text-sm">
            {status === 'confirmed' && (
              <span className="inline-flex items-center gap-1.5 text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                {resultMessage || 'Done.'}
              </span>
            )}
            {status === 'cancelled' && (
              <span className="inline-flex items-center gap-1.5 text-secondary-500 dark:text-secondary-400">
                <XCircle className="h-4 w-4" />
                Cancelled
              </span>
            )}
            {status === 'failed' && (
              <span className="inline-flex items-center gap-1.5 text-red-700 dark:text-red-400">
                <AlertTriangle className="h-4 w-4" />
                {resultMessage || 'Failed'}
              </span>
            )}
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onCancel?.(action)}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm rounded-lg border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm?.(action)}
              disabled={isLoading}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium text-white flex items-center gap-2 disabled:opacity-60 ${
                action.destructive
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {action.destructive ? 'Yes, do it' : 'Confirm'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ActionCard;
